import React, { useState, useRef } from "react";
import { Row, Col, Card, Button, Input, Drawer, Form, Select, Modal, message } from "antd";
import { Scrollbars } from "react-custom-scrollbars";
import Keyboard from "react-simple-keyboard";
import sortBy from "lodash/sortBy";
import { v4 as uuidv4 } from "uuid";
import Close from "../../../../../assets/images/close-x.svg";
import giftVoucher from "../../../../../assets/images/giftVoucher.svg";
import cardType from "../../../../../assets/images/cardType.svg";
import UPI from "../../../../../assets/images/upiIcon.svg";
import CancelArrow from "../../../../../assets/images/cancelArrow.svg";
import { checkGiftCard, upsertGiftCardTransaction } from "../../../../../services";
import useAuthStore from "../../../../../stores/useAuthStore";
import useTransactionStore from "../../../../../stores/useTransactionStore";

const { Option } = Select;

const GiftCardDrawer = ({
  isNewGiftCardFlag,
  setIsNewGiftCardFlag,
  cart,
  posConfig,
  currenciesList,
  currenciesData,
  showPayments,
  setShowPayments,
  setStartIndex,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  requestPayment,
  getFilteredPaymentMethods,
  themeJSON,
  giftCardMode = "topup", // "topup" (from Gift Card menu) | "redeem" (from payment bar)
  handleEnterTopup,
  giftCardTopupUPIInput,
  setGiftCardTopupUPIInput,
  giftCardTopupAmount,
  setGiftCardTopupAmount,
  giftCardTopupPaymentMethod,
  setGiftCardTopupPaymentMethod,
  giftCardTopupCashInput,
  setGiftCardTopupCashInput,
  giftCardTopupCardInput,
  setGiftCardTopupCardInput,
  showGiftCardTopup,
  setShowGiftCardTopup,
  giftCardNumber,
  setGiftCardNumber,
  giftCardBalance,
  setGiftCardBalance,
}) => {
  const tillData = useAuthStore((s) => s.tillData);
  // const [giftCardNumber, setGiftCardNumber] = useState("");
  const [giftCardPin, setGiftCardPin] = useState("");
  const [isCheckingBalance, setIsCheckingBalance] = useState(false);
  const [giftCardTopupFocusedField, setGiftCardTopupFocusedField] = useState("topupAmount");
  const [giftCardTopupDenomKeyboard, setGiftCardTopupDenomKeyboard] = useState(false);
  const [giftCardTopupCardForm] = Form.useForm();
  const [giftCardTopupShowCardAmountInput, setGiftCardTopupShowCardAmountInput] = useState(false);
  const [showGCCardDetailsModal, setShowGCCardDetailsModal] = useState(false);
  const [giftCardTopupInputFocused, setGiftCardTopupInputFocused] = useState("transactionId");
  const [giftCardTopupLayoutName, setGiftCardTopupLayoutName] = useState("default");
  const giftCardTopupKeyboardRef = useRef(null);
  const giftCardNumberRef = useRef(null);
  const giftCardPinRef = useRef(null);
  const giftCardTopupInputRef = useRef(null);
  const giftCardTopupCashRef = useRef(null);
  const giftCardTopupCardRef = useRef(null);
  const giftCardTopupUPIRef = useRef(null);
  // User-editable redeem amount so the cashier can apply a partial gift-card
  // payment. Defaults to the remaining balance below.
  const [giftCardRedeemAmount, setGiftCardRedeemAmount] = useState("");

  if (!isNewGiftCardFlag) return null;

  // Show the OUTSTANDING amount, not the cart total — once partial payments
  // have been made, the gift-card screen and any auto-redeem must work off the
  // remaining balance, otherwise the gift card swallows the whole bill again.
  const cartTotalNum = parseFloat(cart?.total || 0);
  const cartPaidNum = parseFloat(cart?.paid || 0);
  const remainingNum = Math.max(0, cartTotalNum - cartPaidNum);
  const billAmount = !isNaN(remainingNum) ? remainingNum.toFixed(2) : "0.00";
  const currSymbol = currenciesList?.[0]?.symbolRightSide === "N" ? currenciesList[0].currSymbol : "";
  const currSymbolRight = currenciesList?.[0]?.symbolRightSide === "Y" ? currenciesList[0].currSymbol : "";

  const resetGiftCardTopup = () => {
    setShowGiftCardTopup(false);
    setGiftCardTopupAmount("");
    setGiftCardTopupCashInput("");
    setGiftCardTopupCardInput("");
    setGiftCardTopupUPIInput("");
    setGiftCardTopupPaymentMethod(null);
    setGiftCardTopupFocusedField("topupAmount");
    setGiftCardTopupDenomKeyboard(false);
    setGiftCardTopupShowCardAmountInput(false);
    giftCardTopupCardForm.resetFields();
    useTransactionStore.getState().setWalletPayments([]);
    localStorage.removeItem("cardPaymentDetails");
  };

  const resetGiftCardState = () => {
    setIsNewGiftCardFlag(false);
    setGiftCardNumber("");
    setGiftCardPin("");
    setGiftCardBalance(null);
    setGiftCardRedeemAmount("");
    resetGiftCardTopup();
  };

  const closeAll = () => {
    setSelectedPaymentMethod({});
    resetGiftCardState();
    let methods = getFilteredPaymentMethods() || [];
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (isPreOrderPage) {
      const maxPaymentsToShow = 3;
      let payments = methods.length > maxPaymentsToShow ? sortBy(methods, "sequenceNo").slice(0, maxPaymentsToShow) : methods;
      setShowPayments(payments);
      setStartIndex(payments.length);
    }
  };

  const handleCheckGiftCardBalance = async () => {
    if (!giftCardNumber) {
      message.warning("Please enter Gift Card Number");
      return;
    }
    setIsCheckingBalance(true);
    try {
      const data = {
        query: `query {
          checkGiftCardBalance(cardNo: "${giftCardNumber}", cardPin: ${giftCardPin ? `"${giftCardPin}"` : null}){
              currentBalance
              cardGroup
              status
              message
            }
        }`,
      };
      const giftCardResponse = await checkGiftCard(data);
      if (giftCardResponse.data.data.checkGiftCardBalance.length > 0 && giftCardResponse.data.data.checkGiftCardBalance[0].status === "200") {
        const balance = giftCardResponse.data.data.checkGiftCardBalance[0].currentBalance;
        setGiftCardBalance(balance);
        message.success("Gift Card balance retrieved successfully");
      } else {
        message.error(`${giftCardResponse.data.data.checkGiftCardBalance[0].message}`);
        setGiftCardBalance(null);
      }
    } catch (err) {
      message.error("Failed to check Gift Card balance");
      setGiftCardBalance(null);
    } finally {
      setIsCheckingBalance(false);
    }
  };

  const handleGiftCardTopup = () => {
    if (!giftCardNumber) {
      message.warning("Please enter Gift Card Number first");
      return;
    }
    setShowGiftCardTopup(true);
    const cashPayment = sortBy(
      showPayments?.filter((pm) => ["cash", "card", "upi"].includes(pm.name.toLowerCase())),
      ["sequenceNo", "name"]
    )?.[0];
    setGiftCardTopupPaymentMethod(cashPayment || null);
    setGiftCardTopupFocusedField("topupAmount");
    setGiftCardTopupDenomKeyboard(cashPayment?.name?.toLowerCase() === "cash");
  };

  const handleGiftCardTopupKeypad = (value) => {
    let currentValue = "";
    const field = giftCardTopupFocusedField;
    if (field === "topupAmount") currentValue = giftCardTopupAmount;
    else if (field === "cashInput") currentValue = giftCardTopupCashInput;
    else if (field === "cardInput") currentValue = giftCardTopupCardInput;
    else if (field === "upiInput") currentValue = giftCardTopupUPIInput;

    let newValue = "";
    if (value === "clear") newValue = "";
    else if (value === "x") newValue = currentValue.slice(0, -1) || "";
    else if (value === ".") newValue = !currentValue.includes(".") ? currentValue + value : currentValue;
    else newValue = currentValue + value;

    const sanitizedValue = newValue.replace(/[^0-9.]/g, "");
    const parts = sanitizedValue.split(".");
    if (parts.length > 2 || (parts.length === 2 && parts[1].length > 2)) return;

    if (field === "topupAmount") setGiftCardTopupAmount(sanitizedValue);
    else if (field === "cashInput") setGiftCardTopupCashInput(sanitizedValue);
    else if (field === "cardInput") setGiftCardTopupCardInput(sanitizedValue);
    else if (field === "upiInput") setGiftCardTopupUPIInput(sanitizedValue);
  };

  const handleGiftCardTopupDenomClick = (value) => {
    const sanitizedValue = Number.parseFloat(value.toString().replace(/[^0-9.]/g, "")) || 0;
    const field = giftCardTopupFocusedField;
    if (field === "topupAmount") {
      const cv = Number.parseFloat(giftCardTopupAmount) || 0;
      setGiftCardTopupAmount((cv + sanitizedValue).toFixed(2));
    } else if (field === "cashInput") {
      const cv = Number.parseFloat(giftCardTopupCashInput) || 0;
      setGiftCardTopupCashInput((cv + sanitizedValue).toFixed(2));
    } else if (field === "cardInput") {
      const cv = Number.parseFloat(giftCardTopupCardInput) || 0;
      setGiftCardTopupCardInput((cv + sanitizedValue).toFixed(2));
    } else if (field === "upiInput") {
      const cv = Number.parseFloat(giftCardTopupUPIInput) || 0;
      setGiftCardTopupUPIInput((cv + sanitizedValue).toFixed(2));
    }
  };

  const giftCardTopupPaymentMethods = sortBy(
    showPayments?.filter((pm) => ["cash", "card", "upi"].includes(pm.name.toLowerCase())),
    ["sequenceNo", "name"]
  );

  const currentPaymentInput =
    giftCardTopupPaymentMethod?.name?.toLowerCase() === "cash"
      ? giftCardTopupCashInput
      : giftCardTopupPaymentMethod?.name?.toLowerCase() === "card"
        ? giftCardTopupCardInput
        : giftCardTopupUPIInput;

  const rawGiftCardPayments = useTransactionStore.getState().walletPayments || [];
  const giftCardMatchedPayments = rawGiftCardPayments.map((payment) => {
    const matchedMethod = showPayments?.find((method) => method.finPaymentmethodId === payment.finPaymentmethodID);
    return { ...matchedMethod, ...payment, amount: Number(payment.amount).toFixed(2) };
  });
  const giftCardTotalPaid = giftCardMatchedPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  const giftCardAutoFillAmount = (topupAmt, pm) => {
    const method = pm || giftCardTopupPaymentMethod;
    if (!method || !topupAmt || isNaN(topupAmt)) return;
    const remaining = parseFloat(topupAmt) - giftCardTotalPaid;
    if (remaining <= 0) return;
    const value = remaining.toString();
    if (method.name?.toLowerCase() === "cash") setGiftCardTopupCashInput(value);
    else if (method.name?.toLowerCase() === "card") setGiftCardTopupCardInput(value);
    else if (method.name?.toLowerCase() === "upi") setGiftCardTopupUPIInput(value);
  };

  const handleGiftCardTopupAmountChange = (value) => {
    const sanitizedValue = value.replace(/[^0-9.]/g, "");
    const parts = sanitizedValue.split(".");
    if (parts.length > 2) return;
    if (parts.length === 2 && parts[1].length > 2) return;
    setGiftCardTopupAmount(sanitizedValue);
    if (sanitizedValue === "") {
      setGiftCardTopupCashInput("");
      setGiftCardTopupCardInput("");
      setGiftCardTopupUPIInput("");
      return;
    }
    giftCardAutoFillAmount(sanitizedValue);
  };

  // const handleEnterTopup = async () => {
  //   const topupValue = parseFloat(giftCardTopupAmount);
  //   const paymentValue = parseFloat(currentPaymentInput);
  //   if (!topupValue || topupValue <= 0) {
  //     message.warning("Please enter a valid top-up amount");
  //     return;
  //   }
  //   if (!giftCardTopupPaymentMethod) {
  //     message.warning("Please select a payment method");
  //     return;
  //   }
  //   if (!paymentValue || paymentValue <= 0) {
  //     message.warning("Please enter a valid payment amount");
  //     return;
  //   }
  //   const topupTotal = parseFloat(topupValue).toFixed(2);
  //   if (giftCardTotalPaid + paymentValue > parseFloat(topupTotal)) {
  //     const remaining = (parseFloat(topupTotal) - giftCardTotalPaid).toFixed(2);
  //     message.error(`${giftCardTopupPaymentMethod.name} payment exceeds top-up amount. Remaining: ${currSymbol}${remaining}${currSymbolRight}`);
  //     return;
  //   }
  //   if (
  //     giftCardTopupPaymentMethod?.name?.toLowerCase() === "card" &&
  //     !giftCardTopupPaymentMethod?.integratedPayment &&
  //     posConfig?.transactionID === "Y" &&
  //     !localStorage.getItem("cardPaymentDetails")
  //   ) {
  //     setShowGCCardDetailsModal(true);
  //     return;
  //   }
  //   try {
  //     await requestPayment(giftCardTopupPaymentMethod, paymentValue.toFixed(2), undefined, true);
  //     const updatedTotalPaid = (giftCardTotalPaid + paymentValue).toFixed(2);
  //     setGiftCardTopupCashInput("");
  //     setGiftCardTopupCardInput("");
  //     setGiftCardTopupUPIInput("");
  //     giftCardAutoFillAmount(giftCardTopupAmount);
  //     if (parseFloat(updatedTotalPaid) >= parseFloat(topupTotal)) {
  //       try {
  //         const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  //         // Build payments array from all accumulated walletPayments
  //         const latestPayments = useTransactionStore.getState().walletPayments || [];
  //         const latestMatchedPayments = latestPayments.map((payment) => {
  //           const matchedMethod = showPayments?.find((m) => m.finPaymentmethodId === payment.finPaymentmethodID);
  //           return { ...matchedMethod, ...payment };
  //         });
  //         const paymentsGQL = latestMatchedPayments
  //           .map((p) => {
  //             const bankTransferFields =
  //               p.isBankTransfer === "Y"
  //                 ? `
  //     financialAccountId: ${p.financialAccountId ? `"${p.financialAccountId}"` : null}
  //     transactionId: ${p.transactionId ? `"${p.transactionId}"` : null}
  //     transactionTime: ${p.transactionTime ? `"${p.transactionTime}"` : null}
  //     imageUrl: ${p.imageUrl ? `"${p.imageUrl.replace(/\\/g, "\\\\")}"` : null}`
  //                 : "";
  //             return `{
  //     finPaymentmethodID: "${p.finPaymentmethodId || p.finPaymentmethodID}"
  //     amount: ${parseFloat(p.amount)}
  //     ${bankTransferFields}
  //   }`;
  //           })
  //           .join(",");
  //         const upsertDetails = [];
  //         upsertDetails.push(`{
  //           cardNo: ${giftCardNumber ? `"${giftCardNumber}"` : null}
  //           type: "TO"
  //           referenceNo: "${uuidv4().replace(/-/g, "").toUpperCase()}"
  //           amount: ${parseFloat(topupTotal)}
  //           cardPin: ${giftCardPin ? `"${giftCardPin}"` : null}
  //           tillId: ${tillData?.tillAccess?.cwrTill?.cwrTillID ? `"${tillData?.tillAccess?.cwrTill?.cwrTillID}"` : null}
  //           b2c_Customer_Id: ${cart.customer?.cwrCustomerId ? `"${cart.customer.cwrCustomerId}"` : null}
  //           bunitId: "${bunitId}"
  //           payments: [${paymentsGQL}]
  //         }`);
  //         const upsertData = { query: `mutation { upsertGiftCardTransaction(giftCardTransactions: [${upsertDetails}]) { status message } }` };
  //         const topupResponse = await upsertGiftCardTransaction(upsertData);
  //         const result = topupResponse?.data?.data?.upsertGiftCardTransaction;
  //         if (result?.status === "200") {
  //           message.success(`Gift Card topped up with ${currSymbol}${topupTotal}${currSymbolRight}`);
  //           useTransactionStore.getState().setWalletPayments([]);
  //           resetGiftCardTopup();
  //           await handleCheckGiftCardBalance();
  //         } else {
  //           message.error(result?.message || "Gift Card top-up failed");
  //           useTransactionStore.getState().setWalletPayments([]);
  //           resetGiftCardTopup();
  //         }
  //       } catch (topupErr) {
  //         message.error("Gift Card top-up service failed");
  //         useTransactionStore.getState().setWalletPayments([]);
  //         resetGiftCardTopup();
  //       }
  //     } else {
  //       const remaining = (parseFloat(topupTotal) - parseFloat(updatedTotalPaid)).toFixed(2);
  //       message.success(
  //         `${giftCardTopupPaymentMethod.name} payment of ${currSymbol}${paymentValue.toFixed(2)}${currSymbolRight} added. Remaining: ${currSymbol}${remaining}${currSymbolRight}`
  //       );
  //     }
  //   } catch (err) {
  //     message.error("Gift Card top-up failed");
  //   }
  // };

  const handleGCCardDetailsSubmit = async () => {
    try {
      await giftCardTopupCardForm.validateFields();
      const cardValues = giftCardTopupCardForm.getFieldsValue();
      localStorage.setItem("cardPaymentDetails", JSON.stringify(cardValues));
      setShowGCCardDetailsModal(false);
      giftCardTopupCardForm.resetFields();
      await handleEnterTopup();
    } catch {
      message.error("Please fill all required card details");
    }
  };

  return (
    <>
      {/* First Drawer: Gift Card Number + PIN + Check Balance + Top-up Button */}
      <Drawer
        className="customerWalletDrawer"
        open={isNewGiftCardFlag && !showGiftCardTopup}
        closable={false}
        maskClosable={false}
        onClose={closeAll}
        placement="right"
        width="62%"
        headerStyle={{ background: "#f3f4f9", borderBottom: "none" }}
        styles={{ body: { padding: "0.5rem", paddingTop: 0, background: "#f3f4f9", color: "#000" } }}
        style={{ borderRadius: "15px" }}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "1.2rem",
                fontWeight: "550",
                color: "#0F0718",
              }}
            >
              {/* <img
              src={giftVoucher || "/placeholder.svg"}
              style={{
                height: "24px",
                width: "24px",
                objectFit: "contain",
              }}
              alt=""
            /> */}

              <span>{giftCardMode === "redeem" ? "Redeem Gift Card" : "Add Money to Gift Card"}</span>
            </div>
            <img src={Close || "/placeholder.svg"} onClick={closeAll} style={{ cursor: "pointer", width: "1.5vw" }} alt="Close" />
          </div>
        }
      >
        <div style={{ marginLeft: "1.5em", marginRight: "1.5em", paddingTop: "1rem" }}>
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", marginBottom: "0.4rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>
              Gift Card Number <span style={{ color: "#ff4d4f" }}>*</span>
            </label>
            <Input
              ref={giftCardNumberRef}
              value={giftCardNumber}
              onChange={(e) => setGiftCardNumber(e.target.value)}
              onFocus={() => setTimeout(() => giftCardNumberRef.current?.select(), 0)}
              placeholder="Enter Number"
              size="large"
              style={{ borderRadius: "6px", fontSize: "1rem" }}
              onPressEnter={() => giftCardPinRef.current?.focus()}
            />
          </div>
          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", marginBottom: "0.4rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>
              PIN <span style={{ color: "#888", fontWeight: "400" }}>(Optional)</span>
            </label>
            <Input
              ref={giftCardPinRef}
              value={giftCardPin}
              onChange={(e) => setGiftCardPin(e.target.value)}
              placeholder="Enter Pin"
              type="password"
              size="large"
              style={{ borderRadius: "6px", fontSize: "1rem" }}
              onPressEnter={handleCheckGiftCardBalance}
            />
          </div>
          {giftCardBalance !== null && (
            <div
              style={{
                height: "3em",
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontSize: "1rem",
                backgroundColor: "#f0f0f0",
                color: "#28a745",
                fontWeight: "bold",
                borderRadius: "12px",
                marginBottom: "1.5rem",
              }}
            >
              Current Balance: {currSymbol}
              {parseFloat(giftCardBalance).toFixed(2)}
              {currSymbolRight}
            </div>
          )}
          <Row gutter={[16, 16]} style={{ marginBottom: "2rem" }}>
            <Col span={12}>
              <div style={{ background: "#f9f9f9", borderRadius: "8px", padding: "1rem" }}>
                <p style={{ margin: 0, color: "#666", fontSize: "0.85rem", fontWeight: "500" }}>Gift Card Balance</p>
                <h3 style={{ margin: "0.4rem 0 0 0", color: "#0F0718", fontWeight: "700" }}>
                  {giftCardBalance !== null ? `${currSymbol}${parseFloat(giftCardBalance).toFixed(2)}${currSymbolRight}` : "0.00"}
                </h3>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ background: "#f9f9f9", borderRadius: "8px", padding: "1rem" }}>
                <p style={{ margin: 0, color: "#666", fontSize: "0.85rem", fontWeight: "500" }}>Bill Amount</p>
                <h3 style={{ margin: "0.4rem 0 0 0", color: "#0F0718", fontWeight: "700" }}>
                  {currSymbol}
                  {billAmount}
                  {currSymbolRight}
                </h3>
              </div>
            </Col>
          </Row>
          {giftCardMode === "redeem" && giftCardBalance !== null && parseFloat(giftCardBalance) > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ display: "block", marginBottom: "0.4rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Redeem Amount</label>
              <Input
                value={giftCardRedeemAmount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (/^\d*\.?\d*$/.test(v)) setGiftCardRedeemAmount(v);
                }}
                placeholder={`Up to ${currSymbol}${Math.min(parseFloat(giftCardBalance), parseFloat(billAmount)).toFixed(2)}${currSymbolRight}`}
                size="large"
                style={{ borderRadius: "6px", textAlign: "right", fontSize: "1rem" }}
              />
            </div>
          )}
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button
              size="large"
              loading={isCheckingBalance}
              onClick={handleCheckGiftCardBalance}
              style={{ flex: 1, height: "50px", backgroundColor: "#28a745", borderColor: "#28a745", color: "#fff", fontWeight: "600", fontSize: "0.95rem", borderRadius: "8px" }}
            >
              Check Balance
            </Button>
            {giftCardMode === "redeem" ? (
              <Button
                size="large"
                disabled={giftCardBalance === null || parseFloat(giftCardBalance) <= 0}
                onClick={() => {
                  if (!giftCardNumber) {
                    message.warning("Please enter Gift Card Number");
                    return;
                  }
                  const maxRedeem = Math.min(parseFloat(giftCardBalance) || 0, parseFloat(billAmount) || 0);
                  const userEntered = parseFloat(giftCardRedeemAmount);
                  const redeemAmount = !isNaN(userEntered) && userEntered > 0 ? Math.min(userEntered, maxRedeem) : maxRedeem;
                  if (redeemAmount <= 0) {
                    message.warning("No balance to redeem or no bill amount");
                    return;
                  }
                  if (!isNaN(userEntered) && userEntered > maxRedeem) {
                    message.warning(`Amount cannot exceed ${currSymbol}${maxRedeem.toFixed(2)}${currSymbolRight}`);
                    return;
                  }
                  requestPayment(selectedPaymentMethod, redeemAmount, { giftCardNumber, giftCardPin });
                  resetGiftCardState();
                }}
                style={{ flex: 1, height: "50px", backgroundColor: "#2F3856", borderColor: "#2F3856", color: "#fff", fontWeight: "600", fontSize: "0.95rem", borderRadius: "8px" }}
              >
                Redeem Gift Card
              </Button>
            ) : (
              <Button
                size="large"
                onClick={handleGiftCardTopup}
                style={{ flex: 1, height: "50px", backgroundColor: "#2F3856", borderColor: "#2F3856", color: "#fff", fontWeight: "600", fontSize: "0.95rem", borderRadius: "8px" }}
              >
                Top-up Gift Card
              </Button>
            )}
          </div>
        </div>
      </Drawer>

      {/* Second Drawer: Top-up Gift Card */}
      <Drawer
        className="customerWalletDrawer"
        open={isNewGiftCardFlag && showGiftCardTopup}
        closable={false}
        maskClosable={false}
        onClose={resetGiftCardTopup}
        placement="right"
        width="62%"
        headerStyle={{ background: "#fff", borderBottom: "none" }}
        styles={{ body: { padding: "0.5rem", paddingTop: 0, background: "white" } }}
        style={{ borderRadius: "15px" }}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{display: "flex", alignItems: "center", fontSize: "1.2rem", fontWeight: "550", color: "#0F0718"}}>
              {/* <img src={giftVoucher || "/placeholder.svg"} style={{ height: "3.5vh", paddingRight: "5px", paddingLeft: "7px" }} alt="" /> */}
              Top-up Gift Card
            </div>
            <img src={Close || "/placeholder.svg"} onClick={resetGiftCardTopup} style={{ cursor: "pointer", width: "1.5vw" }} alt="Close" />
          </div>
        }
      >
        <div style={{ marginLeft: "1.5em", marginRight: "1.5em" }}>
          <div
            style={{
              height: "2.5em",
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "1rem",
              backgroundColor: "#f0f0f0",
              color: "#28a745",
              fontWeight: "bold",
              marginTop: "0.5rem",
              borderRadius: "12px",
            }}
          >
            Current Balance: {currSymbol}
            {giftCardBalance !== null ? parseFloat(giftCardBalance).toFixed(2) : "0.00"}
            {currSymbolRight}
          </div>
          <Card style={{ borderRadius: "8px", border: "none", background: "transparent" }}
            styles={{
              body: {
                padding: 0,
              },
            }}
          >
            <Row gutter={[10, 10]}>
              <Col span={24}>
                <div>
                  <label style={{ display: "block", marginBottom: "0.3rem", marginTop: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Top-up Amount</label>
                  <Input
                    ref={giftCardTopupInputRef}
                    size="large"
                    value={giftCardTopupAmount}
                    onChange={(e) => handleGiftCardTopupAmountChange(e.target.value)}
                    onFocus={() => setGiftCardTopupFocusedField("topupAmount")}
                    placeholder="0"
                    style={{
                      textAlign: "right",
                      fontSize: "1rem",
                      borderRadius: "6px",
                      border: giftCardTopupFocusedField === "topupAmount" ? "1.5px solid #2F3856" : "1px solid #d9d9d9",
                    }}
                  />
                </div>
              </Col>
              <Col span={24}>
                <Scrollbars style={{ height: "6vh" }}>
                  <Row style={{ fontSize: "1vw", textAlign: "center" }}>
                    <Col span={24}>
                      {giftCardMatchedPayments?.length > 0 ? (
                        giftCardMatchedPayments.map((item, index) => (
                          <div key={index} style={{ display: "flex", justifyContent: "space-between", padding: "2px 8px", fontSize: "0.85rem", color: "#0F0718" }}>
                            <span>{item.name}</span>
                            <span style={{ fontWeight: "600" }}>
                              {currSymbol}
                              {item.amount}
                              {currSymbolRight}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p style={{ margin: "0", color: "#888" }}>No payments added</p>
                      )}
                    </Col>
                  </Row>
                </Scrollbars>
              </Col>
              <Col span={24}>
                <div>
                  <label style={{ display: "block", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Payment Method</label>
                  <div style={{ display: "flex", gap: 5 }}>
                    {giftCardTopupPaymentMethods.map((pm) => (
                      <Button
                        key={pm.name}
                        type={giftCardTopupPaymentMethod?.name === pm.name ? "primary" : "default"}
                        onClick={() => {
                          setGiftCardTopupCashInput("");
                          setGiftCardTopupCardInput("");
                          setGiftCardTopupUPIInput("");
                          setGiftCardTopupPaymentMethod(pm);
                          setGiftCardTopupDenomKeyboard(pm.name.toLowerCase() === "cash");
                          setGiftCardTopupShowCardAmountInput(pm.name.toLowerCase() === "card" && posConfig?.transactionID === "Y" ? false : true);
                          giftCardTopupCardForm.resetFields();
                          localStorage.removeItem("cardPaymentDetails");
                          setGiftCardTopupFocusedField(null);
                          setTimeout(() => {
                            if (pm.name.toLowerCase() === "cash") {
                              giftCardTopupCashRef.current?.focus();
                              setGiftCardTopupFocusedField("cashInput");
                            } else if (pm.name.toLowerCase() === "card") {
                              giftCardTopupCardRef.current?.focus();
                              setGiftCardTopupFocusedField("cardInput");
                            } else if (pm.name.toLowerCase() === "upi") {
                              giftCardTopupUPIRef.current?.focus();
                              setGiftCardTopupFocusedField("upiInput");
                            }
                            giftCardAutoFillAmount(giftCardTopupAmount, pm);
                          }, 0);
                        }}
                        style={{
                          flex: 1,
                          height: "40px",
                          fontSize: "0.9rem",
                          backgroundColor: giftCardTopupPaymentMethod?.name === pm.name ? "#2F3856" : themeJSON.paymentMethodsBGColor || "#fff",
                          color: giftCardTopupPaymentMethod?.name === pm.name ? "#fff" : "#0F0718",
                          borderColor: themeJSON.paymentMethodsBGColor || "#e8e8e8",
                          borderRadius: "7px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {pm.name.toLowerCase() === "cash" ? (
                            <span>
                              {currenciesList?.[0]?.currSymbol || ""} {pm.name}
                            </span>
                          ) : pm.name.toLowerCase() === "card" ? (
                            <span>
                              <img src={cardType} style={{ height: "15px", marginRight: "5px" }} alt="card" />
                              {pm.name}
                            </span>
                          ) : pm.name.toLowerCase() === "upi" ? (
                            <span>
                              <img src={UPI} style={{ height: "15px", marginRight: "5px" }} alt="upi" />
                              {pm.name}
                            </span>
                          ) : (
                            <span>{pm.name}</span>
                          )}
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>
              </Col>

              {giftCardTopupPaymentMethod?.name?.toLowerCase() === "cash" && giftCardTopupDenomKeyboard ? (
                <Col span={24}>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                    <div>
                      <Row>
                        <Input
                          className="productSearch sm-payment-search totalQtyInput"
                          style={{
                            borderRadius: "5px",
                            height: "6vh",
                            margin: "0 0% 2.7% 0%",
                            textAlign: "right",
                            fontWeight: "500",
                            fontSize: "2.5em",
                            color: "#2F3856",
                            fontFamily: "Inter",
                            border: "1px solid #f0f0f0",
                            borderColor: "#fff",
                          }}
                          value={giftCardTopupCashInput}
                          onChange={(e) => setGiftCardTopupCashInput(e.target.value?.replace(/[^\d.]/g, ""))}
                          onFocus={() => setGiftCardTopupFocusedField("cashInput")}
                          ref={giftCardTopupCashRef}
                          placeholder="0"
                        />
                      </Row>
                      <Row style={{ padding: "0 0% 0 0.5%" }}>
                        <Col span={18}>
                          <Row gutter={[8, 5]} style={{ display: "flex", flexDirection: "row-reverse" }}>
                            {currenciesData?.map((currency) => (
                              <Col span={8} key={currency.value}>
                                <button
                                  className="amt-dial-btn-market"
                                  onClick={() => handleGiftCardTopupDenomClick(currency.value)}
                                  disabled={currency.value === 999999}
                                  style={{ color: currency.value !== 999999 ? "#0f0718" : "transparent", background: themeJSON?.keyBoardColor }}
                                >
                                  {currency.value}
                                </button>
                              </Col>
                            ))}
                          </Row>
                        </Col>
                        <Col span={6}>
                          <Row style={{ padding: "0 0 2% 4%" }}>
                            <button className="amt-dial-btn-market" onClick={() => handleGiftCardTopupKeypad("x")} style={{ background: themeJSON?.keyBoardColor }}>
                              <img src={CancelArrow} alt="back space" width={25} height={15} />
                            </button>
                          </Row>
                          <Row style={{ padding: "0 0 2% 4%" }}>
                            <button className="amt-dial-btn-market" onClick={() => setGiftCardTopupDenomKeyboard(false)} style={{ background: themeJSON?.keyBoardColor }}>
                              1,2,3...
                            </button>
                          </Row>
                          <Row style={{ padding: "0 0 0 4%" }}>
                            <button className="amt-dial-btn-market2" onClick={handleEnterTopup} style={{ background: themeJSON?.keyBoardColor }}>
                              Enter
                            </button>
                          </Row>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
              ) : giftCardTopupPaymentMethod?.name?.toLowerCase() === "cash" && !giftCardTopupDenomKeyboard ? (
                <Col span={24}>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                    <div>
                      <Row>
                        <Input
                          className="productSearch sm-payment-search totalQtyInput"
                          style={{
                            borderRadius: "5px",
                            height: "6vh",
                            margin: "0 0% 2.7% 0%",
                            textAlign: "right",
                            fontWeight: "500",
                            fontSize: "2.5em",
                            color: "#2F3856",
                            fontFamily: "Inter",
                            border: "1px solid #f0f0f0",
                            borderColor: "#fff",
                          }}
                          value={giftCardTopupCashInput}
                          onChange={(e) => setGiftCardTopupCashInput(e.target.value?.replace(/[^\d.]/g, ""))}
                          onFocus={() => setGiftCardTopupFocusedField("cashInput")}
                          ref={giftCardTopupCashRef}
                          placeholder="0"
                        />
                      </Row>
                      <Row style={{ padding: "0 0% 0 0.5%" }} gutter={[8, 6]}>
                        {["1", "2", "3"].map((n) => (
                          <Col span={6} key={n}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                              {n}
                            </button>
                          </Col>
                        ))}
                        <Col span={6}>
                          <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("x")}>
                            <img src={CancelArrow} alt="back space" width={25} height={15} />
                          </button>
                        </Col>
                        {["4", "5", "6"].map((n) => (
                          <Col span={6} key={n}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                              {n}
                            </button>
                          </Col>
                        ))}
                        <Col span={6}>
                          <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => setGiftCardTopupDenomKeyboard(true)}>
                            1,2,3...
                          </button>
                        </Col>
                        <Col span={18}>
                          <Row gutter={[8, 6]}>
                            {["7", "8", "9"].map((n) => (
                              <Col span={8} key={n}>
                                <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                                  {n}
                                </button>
                              </Col>
                            ))}
                            <Col span={8}>
                              <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("0")}>
                                0
                              </button>
                            </Col>
                            <Col span={8}>
                              <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(".")}>
                                .
                              </button>
                            </Col>
                            <Col span={8}>
                              <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("clear")}>
                                Clear
                              </button>
                            </Col>
                          </Row>
                        </Col>
                        <Col span={6}>
                          <button className="amt-dial-btn-market2" style={{ background: themeJSON?.keyBoardColor }} onClick={handleEnterTopup}>
                            Enter
                          </button>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
              ) : giftCardTopupPaymentMethod?.name?.toLowerCase() === "card" ? (
                <Col span={24}>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                    <Input
                      ref={giftCardTopupCardRef}
                      className="productSearch sm-payment-search totalQtyInput"
                      style={{ borderRadius: "5px", height: "6vh", textAlign: "right", fontWeight: "500", fontSize: "1.5em", border: "1px solid #f0f0f0" }}
                      value={giftCardTopupCardInput}
                      onChange={(e) => setGiftCardTopupCardInput(e.target.value?.replace(/[^\d.]/g, ""))}
                      onFocus={() => setGiftCardTopupFocusedField("cardInput")}
                      placeholder="0"
                    />
                    <Row style={{ padding: "0.5rem 0" }} gutter={[8, 6]}>
                      {["1", "2", "3"].map((n) => (
                        <Col span={6} key={n}>
                          <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                            {n}
                          </button>
                        </Col>
                      ))}
                      <Col span={6}>
                        <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("x")}>
                          <img src={CancelArrow} alt="back space" width={25} height={15} />
                        </button>
                      </Col>
                      {["4", "5", "6"].map((n) => (
                        <Col span={6} key={n}>
                          <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                            {n}
                          </button>
                        </Col>
                      ))}
                      <Col span={6}>
                        <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("clear")}>
                          Clear
                        </button>
                      </Col>
                      <Col span={18}>
                        <Row gutter={[8, 6]}>
                          {["7", "8", "9"].map((n) => (
                            <Col span={8} key={n}>
                              <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={8}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("0")}>
                              0
                            </button>
                          </Col>
                          <Col span={8}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(".")}>
                              .
                            </button>
                          </Col>
                          <Col span={8}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("00")}>
                              00
                            </button>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={6}>
                        <button className="amt-dial-btn-market2" style={{ background: themeJSON?.keyBoardColor }} onClick={handleEnterTopup}>
                          Enter
                        </button>
                      </Col>
                    </Row>
                  </div>
                </Col>
              ) : giftCardTopupPaymentMethod?.name?.toLowerCase() === "upi" ? (
                <Col span={24}>
                  <div>
                    <label style={{ display: "block", marginBottom: "0.3rem", color: "#0F0718", fontWeight: "550", fontSize: "0.9rem" }}>Enter Amount</label>
                    <Input
                      ref={giftCardTopupUPIRef}
                      className="productSearch sm-payment-search totalQtyInput"
                      style={{ borderRadius: "5px", height: "6vh", textAlign: "right", fontWeight: "500", fontSize: "1.5em", border: "1px solid #f0f0f0" }}
                      value={giftCardTopupUPIInput}
                      onChange={(e) => setGiftCardTopupUPIInput(e.target.value?.replace(/[^\d.]/g, ""))}
                      onFocus={() => setGiftCardTopupFocusedField("upiInput")}
                      placeholder="0"
                    />
                    <Row style={{ padding: "0.5rem 0" }} gutter={[8, 6]}>
                      {["1", "2", "3"].map((n) => (
                        <Col span={6} key={n}>
                          <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                            {n}
                          </button>
                        </Col>
                      ))}
                      <Col span={6}>
                        <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("x")}>
                          <img src={CancelArrow} alt="back space" width={25} height={15} />
                        </button>
                      </Col>
                      {["4", "5", "6"].map((n) => (
                        <Col span={6} key={n}>
                          <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                            {n}
                          </button>
                        </Col>
                      ))}
                      <Col span={6}>
                        <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("clear")}>
                          Clear
                        </button>
                      </Col>
                      <Col span={18}>
                        <Row gutter={[8, 6]}>
                          {["7", "8", "9"].map((n) => (
                            <Col span={8} key={n}>
                              <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(n)}>
                                {n}
                              </button>
                            </Col>
                          ))}
                          <Col span={8}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("0")}>
                              0
                            </button>
                          </Col>
                          <Col span={8}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad(".")}>
                              .
                            </button>
                          </Col>
                          <Col span={8}>
                            <button className="amt-dial-btn-market" style={{ background: themeJSON?.keyBoardColor }} onClick={() => handleGiftCardTopupKeypad("00")}>
                              00
                            </button>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={6}>
                        <button className="amt-dial-btn-market2" style={{ background: themeJSON?.keyBoardColor }} onClick={handleEnterTopup}>
                          Enter
                        </button>
                      </Col>
                    </Row>
                  </div>
                </Col>
              ) : null}
            </Row>
          </Card>
        </div>
      </Drawer>
      <Modal title="Card Payment Details" open={showGCCardDetailsModal} onCancel={() => setShowGCCardDetailsModal(false)} footer={null} destroyOnClose>
        <Form layout="vertical" form={giftCardTopupCardForm} onFinish={handleGCCardDetailsSubmit}>
          {posConfig?.transactionID === "Y" && (
            <Form.Item name="transactionId" label="Transaction ID" rules={[{ required: posConfig?.transactionID_mandatory === "Yes", message: "Please enter Transaction ID" }]}>
              <Input placeholder="Transaction ID" size="large" />
            </Form.Item>
          )}
          {posConfig?.cardType === "Y" && (
            <Form.Item name="cardType" label="Card Type" rules={[{ required: posConfig?.cardType_mandatory === "Yes", message: "Please select Card Type" }]}>
              <Select placeholder="Card Type" size="large">
                <Select.Option value="VI">Visa - VI</Select.Option>
                <Select.Option value="MA">Master Card - MA</Select.Option>
                <Select.Option value="RU">Rupay - RU</Select.Option>
                <Select.Option value="AM">Amex - AM</Select.Option>
                <Select.Option value="ME">Maestro - ME</Select.Option>
              </Select>
            </Form.Item>
          )}
          {posConfig?.CardNumber === "Y" && (
            <Form.Item
              name="cardNumber"
              label="Card Number (Last 4 Digits)"
              rules={[
                { required: posConfig?.CardNumber_mandatory === "Yes", message: "Please enter Card Number" },
                { pattern: /^\d{4}$/, message: "Must be exactly 4 digits" },
              ]}
            >
              <Input placeholder="Last 4 digits" size="large" maxLength={4} />
            </Form.Item>
          )}
          <Button type="primary" htmlType="submit" block size="large" style={{ marginTop: "1rem" }}>
            Process Payment
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default GiftCardDrawer;
